import React, {useEffect, useState} from "react";
import {Header} from "./UserList.styled";
import styled from "styled-components";
import {FormControl, InputLabel, MenuItem, Select, TextField, OutlinedInput} from "@mui/material";
import {User, UserRole} from "../../models/models";
import axios from "axios";
import {useLocation, useNavigate} from "react-router-dom";
import {useSnackbar} from "../../contexts/SnackbarContext";
import BackButton from "../../components/common/BackButton";
import FieldHelpSuffix from "../../components/common/FieldHelpSuffix";
import {ActionButton} from "../../components/common/ActionButtons";
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword} from "../../utils/password-validation";
import {media} from '../../theme/breakpoints';
import {StyledContainer} from "../workspace/UserDataSourceList.styled";
import {useUser} from "../../core/UserContext";

const UserEditWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;

    ${media.md} {
        grid-template-columns: 1fr 1fr;
    }
`;

const StyledTextField = styled(TextField)`
    width: 100%;
    max-width: none;
`;

const StyledFormControl = styled(FormControl)`
    width: 100%;
    max-width: none;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;

    button {
        width: 100%;
    }

    ${media.sm} {
        flex-direction: row;

        button {
            width: auto;
        }

        justify-content: flex-end;
    }
`;

const UserEdit: React.FC = () => {
    const {user: loggedUser, fetchUserData} = useUser();
    const [user, setUser] = useState<User | null>(null);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [passwordsMismatch, setPasswordsMismatch] = useState(false);
    const [passwordError, setPasswordError] = useState<string>("");
    const [resendingWelcome, setResendingWelcome] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const queryParams = new URLSearchParams(location.search);
    const activated = queryParams.get("activated");
    const role = queryParams.get("role");
    const email = queryParams.get("email");
    const createdAt = queryParams.get("createdAt");
    const position = queryParams.get("position");
    const phoneNumber = queryParams.get("phoneNumber");
    const id = Number(queryParams.get("id")) || undefined;

    const sanitizeQueryParam = (val: string | null): string => {
        if (val === null) return "";
        const lowered = val.trim().toLowerCase();
        return lowered === "null" || lowered === "undefined" ? "" : val;
    };

    const nullIfEmpty = (val: string | undefined | null) => {
        if (val === undefined || val === null) return null;
        return val.toString().trim() === "" ? null : val;
    };

    useEffect(() => {
        if (!activated || !role || !email || !createdAt) {
            navigate(-1);
        } else {
            setUser({
                id,
                email,
                createdAt,
                role: role as UserRole,
                activated: activated === 'true',
                position: sanitizeQueryParam(position),
                phoneNumber: sanitizeQueryParam(phoneNumber)
            });
        }
    }, [activated, role, email, createdAt, position, phoneNumber, navigate]);

    const handleInputChange = (field: keyof User, value: any) => {
        setUser((prev) => prev && {...prev, [field]: value});
    };

    useEffect(() => {
        setPasswordsMismatch(!!password && !!confirmPassword && password !== confirmPassword);
    }, [password, confirmPassword]);

    const handleSubmit = async () => {
        try {
            if (password) {
                const error = validatePassword(password, t);
                if (error) {
                    setPasswordError(error);
                    return;
                }
            }

            if (password !== confirmPassword) {
                setPasswordsMismatch(true);
                return;
            }

            const updateData: any = {};

            if (password) {
                updateData.newPassword = password;
            }

            updateData.role = user?.role;
            updateData.isActive = user?.activated;
            updateData.position = nullIfEmpty(user?.position ?? null);
            updateData.phoneNumber = nullIfEmpty(user?.phoneNumber ?? null)

            await axios.patch(`/users?userEmail=${user?.email || ""}`, updateData);
            showSnackbar(t('userEdit.updateSuccess'), "success");

            if (loggedUser?.id === user?.id && loggedUser?.role !== user?.role) {
                await fetchUserData();
                navigate("/");
            } else {
                navigate(-1);
            }
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const handleResendWelcomeEmail = async (userId: number | undefined) => {
        setResendingWelcome(true);
        try {
            await axios.post(`/users/${userId}/send-welcome-email`);
            showSnackbar(t('userEdit.welcomeEmailResent'), 'success');
        } catch (error) {
            showSnackbar(parseError(error), 'error');
        } finally {
            setResendingWelcome(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <StyledContainer role="region" aria-labelledby="user-edit-title">
            <Header>
                <h2 id="user-edit-title">{t('userEdit.title')}</h2>
            </Header>
            <UserEditWrapper>
                <FormGrid>
                    <StyledTextField
                        label={t('userEdit.emailLabel')}
                        value={user.email}
                        disabled
                        fullWidth
                        variant="outlined"
                        inputProps={{ 'aria-label': String(t('userEdit.emailLabel')) }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.email')}
                                    ariaLabel={t('createUser.help.email') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('userEdit.createdDateLabel')}
                        value={new Date(user.createdAt).toLocaleString('pl-PL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                            timeZone: 'Europe/Warsaw',
                        })}
                        disabled
                        fullWidth
                        variant="outlined"
                        inputProps={{ 'aria-label': String(t('userEdit.createdDateLabel')) }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('userEdit.help.createdAt')}
                                    ariaLabel={t('userEdit.help.createdAt') as string}
                                />
                            ),
                        }}
                    />
                    <StyledFormControl fullWidth variant="outlined">
                        <InputLabel>{t('userEdit.roleLabel')}</InputLabel>
                        <Select
                            value={user.role}
                            onChange={(e) => handleInputChange("role", e.target.value)}
                            label={t('userEdit.roleLabel')}
                            sx={{
                                '& .MuiSelect-icon': {
                                    right: 40,
                                },
                            }}
                            input={
                                <OutlinedInput
                                    label={t('userEdit.roleLabel')}
                                    inputProps={{ 'aria-label': String(t('userEdit.roleLabel')) }}
                                    endAdornment={
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userEdit.help.role')}
                                            ariaLabel={t('userEdit.help.role') as string}
                                        />
                                    }
                                    sx={{ pr: '72px' }}
                                />
                            }
                        >
                            <MenuItem value={UserRole.SYSTEM_ADMIN}>{t('userEdit.roleOptions.administrator')}</MenuItem>
                            <MenuItem value={UserRole.USER}>{t('userEdit.roleOptions.user')}</MenuItem>
                        </Select>
                    </StyledFormControl>
                    <StyledFormControl fullWidth variant="outlined">
                        <InputLabel>{t('userEdit.statusLabel')}</InputLabel>
                        <Select
                            value={user.activated.toString()}
                            onChange={(e) =>
                                handleInputChange("activated", e.target.value === "true")
                            }
                            label={t('userEdit.statusLabel')}
                            sx={{
                                '& .MuiSelect-icon': {
                                    right: 40,
                                },
                            }}
                            input={
                                <OutlinedInput
                                    label={t('userEdit.statusLabel')}
                                    inputProps={{ 'aria-label': String(t('userEdit.statusLabel')) }}
                                    endAdornment={
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.status')}
                                            ariaLabel={t('createUser.help.status') as string}
                                        />
                                    }
                                    sx={{ pr: '72px' }}
                                />
                            }
                        >
                            <MenuItem value="true">{t('userEdit.statusOptions.active')}</MenuItem>
                            <MenuItem value="false">{t('userEdit.statusOptions.inactive')}</MenuItem>
                        </Select>
                    </StyledFormControl>
                    <StyledTextField
                        label={t('userEdit.positionLabel')}
                        value={user.position || ""}
                        onChange={(e) => handleInputChange("position", e.target.value)}
                        fullWidth
                        variant="outlined"
                        inputProps={{ 'aria-label': String(t('userEdit.positionLabel')) }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.position')}
                                    ariaLabel={t('createUser.help.position') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('userEdit.phoneNumberLabel')}
                        type="tel"
                        inputProps={{
                            'aria-label': String(t('userEdit.phoneNumberLabel')),
                            inputMode: "numeric",
                            pattern: "^\\+?[0-9]*$",
                            maxLength: 15
                        }}
                        value={user.phoneNumber || ""}
                        onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^+\d]/g, "");
                            handleInputChange("phoneNumber", cleaned);
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.phoneNumber')}
                                    ariaLabel={t('createUser.help.phoneNumber') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('userEdit.newPasswordLabel')}
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError("");
                        }}
                        onBlur={() => setPasswordError(password ? validatePassword(password, t) ?? "" : "")}
                        fullWidth
                        variant="outlined"
                        error={!!passwordError}
                        helperText={passwordError}
                        inputProps={{ 'aria-label': String(t('userEdit.newPasswordLabel')) }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.password')}
                                    ariaLabel={t('createUser.help.password') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.repeatPassword')}
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        variant="outlined"
                        error={Boolean(passwordsMismatch)}
                        helperText={passwordsMismatch ? t("passwordValidation.match") : ""}
                        inputProps={{ 'aria-label': String(t('createUser.repeatPassword')) }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.repeatPassword')}
                                    ariaLabel={t('createUser.help.repeatPassword') as string}
                                />
                            ),
                        }}
                    />
                </FormGrid>
                <ButtonGroup>
                    <BackButton text={t('userEdit.cancelButton') as string}/>
                    <ActionButton onClick={() => handleResendWelcomeEmail(Number(user?.id ?? user?.userId))}
                                  disabled={resendingWelcome || !(user?.id ?? user?.userId)}>
                        {resendingWelcome ? t('userEdit.resendWelcome.sending') : t('userEdit.resendWelcome.button')}
                    </ActionButton>
                    <ActionButton onClick={handleSubmit}>
                        {t('userEdit.saveButton')}
                    </ActionButton>
                </ButtonGroup>
            </UserEditWrapper>
        </StyledContainer>
    )
        ;
};

export default UserEdit;
